import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  TextField,
  ListBase,
  ListToolbar,
  Pagination,
  useRefresh,
  Title,
  Datagrid,
  useListContext,
  ListContextProvider,
  ReferenceField,
} from 'react-admin';

import { Divider, Tabs, Tab, Card, makeStyles } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';

import ActionsList from '../../Components/ReactAdmin/ActionsList';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { formatDateDDMMYYYYHHmm, SECONDARY_COLOR } from '../../utils';
import { EVENTO_MANIFESTACAO_NFE_CIENCIA_DA_OPERACAO } from '../../utils/constants';
import { PERMISSAO_TELA_NFES_CIENTES_LISTAR } from '../../utils/dadosTelas';
import DialogoForcarDownloadXmls from './DialogoForcarDownloadXmls';
import DialogoManifestacao from './DialogoManifestacao';
import DialogoMarcarRecebimento from './DialogoMarcarRecebimento';
import DialogoPdf from './DialogoPdf';
import CnpjField from './Fields/CnpjField';
import DataEmissaoField from './Fields/DataEmissaoField';
import DiasApartirDaEmissaoField from './Fields/DiasApartirDaEmissaoField';
import ValorField from './Fields/ValorField';
import Filter from './Filter';

const useStyles = makeStyles((theme) => ({
  dataHeader: {
    flex: 1,
    backgroundColor: SECONDARY_COLOR,
    padding: '10px',
    color: 'white',
    fontWeight: 'bold',
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
}));

const PostPanel = ({ record }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    <div>
      <span
        style={{
          fontWeight: 'bold',
          marginRight: '10px',
        }}
      >
        Chave de Acesso:
      </span>
      <span>{record.chave}</span>
    </div>
    <div>
      <span
        style={{
          fontWeight: 'bold',
          marginRight: '10px',
        }}
      >
        Descrição do Problema no Recebimento:
      </span>
      <span>{record.descricao_problema}</span>
    </div>
    <div>
      <span
        style={{
          fontWeight: 'bold',
          marginRight: '10px',
        }}
      >
        Data e Hora do Recebimento:
      </span>
      <span>{formatDateDDMMYYYYHHmm(record.data_recebimento)}</span>
    </div>
  </div>
);

const tabs = [
  { id: 'recebido', name: 'recebido' },
  { id: 'não-recebido', name: 'não recebido' },
  { id: 'sem-xml', name: 'sem xml' },
];
//
const MyList = ({ ...props }) => {
  const refresh = useRefresh();
  const classes = useStyles();
  const refDialogoManifestacao = useRef(null);
  const refDialogoPdf = useRef(null);
  const refDialogoMarcarRecebimento = useRef(null);
  const [confirmados, setConfirmados] = useState([]);
  const [cancelados, setCancelados] = useState([]);
  const [semXml, setSemXml] = useState([]);
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;

  const refDialogoForcarDownloadXmls = useRef(null);

  const activeTab =
    filterValues.has_xml === false
      ? 'sem-xml'
      : filterValues.recebido === false
      ? 'não-recebido'
      : 'recebido';

  useEffect(() => {
    if (!ids) return;

    if (filterValues.has_xml === false) {
      setSemXml(ids);
    } else if (filterValues.recebido === false) {
      setCancelados(ids);
    } else {
      setConfirmados(ids);
    }
  }, [ids, filterValues.recebido, filterValues.has_xml]);

  const handleChange = useCallback(
    (event, value) => {
      if (!setFilters) return;

      if (value === 'sem-xml') {
        setFilters(
          { ...filterValues, has_xml: false, recebido: undefined },
          displayedFilters,
        );
      } else {
        setFilters(
          {
            ...filterValues,
            has_xml: undefined,
            recebido: value === 'recebido',
          },
          displayedFilters,
        );
      }
    },
    [displayedFilters, filterValues, setFilters],
  );

  function handleImportar(dados) {
    if (refDialogoManifestacao.current) {
      refDialogoManifestacao.current.handleOpen(dados.chave);
    }
  }

  function handlePDF(dados) {
    if (refDialogoPdf.current) {
      refDialogoPdf.current.handleOpen(dados.chave);
    }
  }

  function handleMarcarComoRecebido(dados) {
    if (refDialogoMarcarRecebimento.current) {
      refDialogoMarcarRecebimento.current.handleOpen(dados.id);
    }
  }

  function handleForcarDownloads(dados) {
    if (refDialogoForcarDownloadXmls.current) {
      refDialogoForcarDownloadXmls.current.handleOpen(dados?.chave || '');
    }
  }

  const handleClose = () => {
    refresh();
  };

  return (
    <>
      <Title title="NFEs Cientes" />
      <ListToolbar
        filters={props.filters}
        actions={
          <ActionsList
            {...props}
            actions={[
              {
                label: 'Forçar Download do XML',
                onClick: () => handleForcarDownloads(),
                icon: <CloudDownloadIcon />,
              },
            ]}
          />
        }
        {...props}
      />
      <Card style={{ borderRadius: '20px' }} {...props}>
        <Tabs
          variant="fullWidth"
          centered
          value={activeTab}
          indicatorColor="primary"
          onChange={handleChange}
        >
          {tabs.map((choice) => (
            <Tab key={choice.id} label={choice.name} value={choice.id} />
          ))}
        </Tabs>
        <Divider />
        <div>
          {activeTab === 'recebido' && (
            <ListContextProvider value={{ ...listContext, ids: confirmados }}>
              <Datagrid {...props}>
                <TextField
                  source="nsu"
                  label="NSU"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <TextField
                  source="emitRazao"
                  label="Razão Social do Emitente"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <CnpjField
                  source="emitCnpj"
                  label="CNPJ do Emitente"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <DataEmissaoField
                  source="dhEmis"
                  label="Data da Emissão"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <ValorField
                  source="vNF"
                  label="Valor da NFE"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <DiasApartirDaEmissaoField
                  source="dhEmis"
                  label="Dia Apartir da Emissão"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <ReferenceField
                  label="Recebedor"
                  source="recebedor_user_id"
                  reference="users"
                  linkType={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                >
                  <TextField source="username" />
                </ReferenceField>
                <ActionsField
                  source="valor"
                  textAlign="right"
                  label="Ações"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                  actions={[
                    {
                      key: 'importar_na_compra',
                      title: 'Importar na Compra',
                      icon: <ShoppingCartIcon />,
                      onClick: (rec) => handleImportar(rec),
                      show: (rec) =>
                        rec.tipo_manifestacao ===
                        EVENTO_MANIFESTACAO_NFE_CIENCIA_DA_OPERACAO,
                    },
                    {
                      key: 'exibir_pdf',
                      title: 'Exibir PDF',
                      icon: <AssignmentIcon />,
                      onClick: (rec) => handlePDF(rec),
                      show: (rec) => true,
                    },
                  ]}
                />
              </Datagrid>
            </ListContextProvider>
          )}
          {activeTab === 'não-recebido' && (
            <ListContextProvider value={{ ...listContext, ids: cancelados }}>
              <Datagrid {...props}>
                <TextField
                  source="nsu"
                  label="NSU"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <TextField
                  source="emitRazao"
                  label="Razão Social do Emitente"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <CnpjField
                  source="emitCnpj"
                  label="CNPJ do Emitente"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <DataEmissaoField
                  source="dhEmis"
                  label="Data da Emissão"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <ValorField
                  source="vNF"
                  label="Valor da NFE"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <DiasApartirDaEmissaoField
                  source="dhEmis"
                  label="Dia Apartir da Emissão"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <ActionsField
                  source="valor"
                  textAlign="right"
                  label="Ações"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                  actions={[
                    /* {
                      key: 'importar_na_compra',
                      title: 'Importar na Compra',
                      icon: <ShoppingCartIcon />,
                      onClick: (rec) => handleImportar(rec),
                      show: (rec) =>
                        rec.tipo_manifestacao ===
                        EVENTO_MANIFESTACAO_NFE_CIENCIA_DA_OPERACAO,
                    }, */
                    {
                      key: 'marcar_como_recebido',
                      title: 'Marcar como Recebido',
                      icon: <LocalShippingIcon />,
                      onClick: (rec) => handleMarcarComoRecebido(rec),
                      show: (rec) => true,
                    },
                    {
                      key: 'exibir_pdf',
                      title: 'Exibir PDF',
                      icon: <AssignmentIcon />,
                      onClick: (rec) => handlePDF(rec),
                      show: (rec) => true,
                    },
                  ]}
                />
              </Datagrid>
            </ListContextProvider>
          )}
          {activeTab === 'sem-xml' && (
            <ListContextProvider value={{ ...listContext, ids: semXml }}>
              <Datagrid {...props}>
                <TextField
                  source="nsu"
                  label="NSU"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <TextField
                  source="emitRazao"
                  label="Razão Social do Emitente"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <CnpjField
                  source="emitCnpj"
                  label="CNPJ do Emitente"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <DataEmissaoField
                  source="dhEmis"
                  label="Data da Emissão"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <ValorField
                  source="vNF"
                  label="Valor da NFE"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <DiasApartirDaEmissaoField
                  source="dhEmis"
                  label="Dias Sem XML"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                />
                <ActionsField
                  source="valor"
                  textAlign="right"
                  label="Ações"
                  sortable={false}
                  headerClassName={classes.dataHeader}
                  cellClassName={classes.dataCell}
                  actions={[
                    {
                      key: 'forcar_download_xml',
                      title: 'Forçar Download do XML',
                      icon: <CloudDownloadIcon />,
                      onClick: (rec) => handleForcarDownloads(rec),
                      show: (rec) => true,
                    },
                  ]}
                />
              </Datagrid>
            </ListContextProvider>
          )}
        </div>
        <TransacoesPagination />
      </Card>
      <DialogoManifestacao
        ref={refDialogoManifestacao}
        handleClose={handleClose}
      />
      <DialogoPdf ref={refDialogoPdf} handleClose={handleClose} />
      <DialogoMarcarRecebimento
        ref={refDialogoMarcarRecebimento}
        handleClose={handleClose}
      />
      <DialogoForcarDownloadXmls
        ref={refDialogoForcarDownloadXmls}
        handleCloseNotification={() => {
          refresh();
        }}
      />
    </>
  );
};

const ContainerMyList = ({ children, ...props }) => (
  <>
    <ListBase {...props}>
      <MyList {...props} />
    </ListBase>
  </>
);

const TransacoesPagination = (props) => (
  <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
);

const List = (props) => (
  <ProtegerPagina permissao={PERMISSAO_TELA_NFES_CIENTES_LISTAR}>
    <ContainerMyList
      {...props}
      expand={<PostPanel />}
      pagination={<TransacoesPagination />}
      perPage={50}
      filters={<Filter />}
      title="NFEs Cientes"
      component="div"
      filterDefaultValues={{
        recebido: true,
      }}
    />
  </ProtegerPagina>
);

export default List;
